// dwaar/src/pages/Samiksha.jsx
// Repo: terralekha-core | Folder: dwaar/src/pages/
// Samiksha — Sanskrit: समीक्षा — review, examination.
//   PassPort detail page + multi-party confirmation panel.
//   Three-step OTP-verified flow: data_accuracy → scope_approval → final_seal.
//   Same user (admin) can complete all three steps (solo MSME mode).
//   Role enforcement is applied server-side; UI reflects the current state.
//
// Data flow:
//   1. auth.getSession()                       → userId + accessToken
//   2. public.users WHERE id = userId          → gstin, mobile, role
//   3. public.passports WHERE id = passportId  → passport details
//   4. GET /api/passports/{id}/confirmations   → confirmation status (via fetch + Bearer)
//
// OTP flow per step:
//   a. User clicks "Confirm via SMS" or "Confirm via WhatsApp"
//   b. POST /api/passports/{id}/confirm/send   → dispatches OTP
//   c. OTP input appears; user types code
//   d. POST /api/passports/{id}/confirm/verify → verifies + inserts confirmation
//   e. Panel refreshes from API; next step becomes active
//
// Reveal doctrine:
//   Internal API errors stay in console. User sees only PUBLIC_ERROR or
//   a safe domain message (e.g. "OTP is incorrect or has expired").

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getSupabaseClient } from "../lib/supabaseClient.js";
import { PASSPORT_LABEL, PUBLIC_ERROR } from "../lib/brand.js";
import OraPanel from "../components/OraPanel.jsx";
import Koshtha from "../components/Koshtha.jsx";
import PaymentGate from "../components/PaymentGate.jsx";

// ── Config ────────────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL ?? "";

// ── Constants ─────────────────────────────────────────────────────────────────

const PHASE = Object.freeze({
  LOADING: "loading",
  READY:   "ready",
  ERROR:   "error",
});

const CONFIRM_STEPS = [
  { type: "data_accuracy",  label: "Data Accuracy",  desc: "Verify that all emission data is accurate and complete." },
  { type: "scope_approval", label: "Scope Approval",  desc: "Approve the emissions scope and methodology." },
  { type: "final_seal",     label: "Final Seal",      desc: "Authorise final sealing of this PassPort." },
];

const STATUS_LABELS = {
  draft:                "DRAFT",
  pending_confirmation: "PENDING",
  sealed:               "SEALED",
  verified:             "VERIFIED",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatPeriod(start, end) {
  const fmt = new Intl.DateTimeFormat("en-IN", { month: "short", year: "numeric" });
  try {
    return (
      fmt.format(new Date(start + "T00:00:00")) +
      " – " +
      fmt.format(new Date(end + "T00:00:00"))
    );
  } catch {
    return `${start} – ${end}`;
  }
}

function formatConfirmedAt(isoStr) {
  if (!isoStr) return "";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(isoStr));
  } catch {
    return isoStr;
  }
}

// ── ConfirmStep sub-component ─────────────────────────────────────────────────

function ConfirmStep({
  step,         // { type, label, desc }
  state,        // "done" | "active" | "locked"
  confirmedAt,  // ISO string if done
  onConfirm,    // () => void
  confirming,   // bool — loading state
  error,        // string | null
}) {
  const isDone   = state === "done";
  const isActive = state === "active";
  const isLocked = state === "locked";

  const stepClass = [
    "tl-confirm-step",
    isDone   ? "tl-confirm-step--done"   : "",
    isActive ? "tl-confirm-step--active" : "",
    isLocked ? "tl-confirm-step--locked" : "",
  ].filter(Boolean).join(" ");

  return (
    <li className={stepClass} aria-label={step.label}>
      <div className="tl-confirm-step-header">
        <span className="tl-confirm-step-label">{step.label}</span>
        {isDone   && <span className="tl-confirm-step-done-text">✓ Confirmed</span>}
        {isLocked && <span className="tl-confirm-step-locked-text">Locked</span>}
      </div>

      <p className="tl-confirm-step-meta">{step.desc}</p>

      {isDone && confirmedAt && (
        <p className="tl-confirm-step-meta" style={{ marginTop: "0.35rem" }}>
          Confirmed {formatConfirmedAt(confirmedAt)}
        </p>
      )}

      {isActive && (
        <div style={{ marginTop: "0.75rem" }}>
          <button
            className="tl-button-confirm-step"
            aria-label={`Confirm ${step.label}`}
            disabled={confirming}
            onClick={onConfirm}
          >
            {confirming ? "Confirming…" : "Confirm"}
          </button>
          {error && (
            <p className="tl-error" role="alert" style={{ marginTop: "0.5rem" }}>
              {error}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

// ── Samiksha ──────────────────────────────────────────────────────────────────

export default function Samiksha() {
  const { id: passportId } = useParams();
  const navigate = useNavigate();

  const [phase,        setPhase]        = useState(PHASE.LOADING);
  const [passport,     setPassport]     = useState(null);
  const [accessToken,  setAccessToken]  = useState("");
  const [userRow,      setUserRow]      = useState(null);
  const [userEmail,    setUserEmail]    = useState("");
  const [orgName,      setOrgName]      = useState("");
  const [confirmations, setConfirmations] = useState([]);
  const [allConfirmed, setAllConfirmed] = useState(false);
  const [nextRequired, setNextRequired] = useState(null);

  const [confirming,     setConfirming]     = useState(null);   // stepType | null
  const [confirmErrors,  setConfirmErrors]  = useState({});
  const [downloadError,  setDownloadError]  = useState("");

  // Data entry form state
  const [prodTonnes,    setProdTonnes]    = useState("");
  const [scope1,        setScope1]        = useState("");
  const [scope2,        setScope2]        = useState("");
  const [dataSaving,    setDataSaving]    = useState(false);
  const [dataSaveError, setDataSaveError] = useState("");
  const [dataSaved,     setDataSaved]     = useState(false);

  // ── Load ──────────────────────────────────────────────────────────────────
  const loadConfirmations = useCallback(async (token) => {
    try {
      const res = await fetch(`${API_BASE}/api/passports/${passportId}/confirmations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`confirmations fetch ${res.status}`);
      const data = await res.json();
      setConfirmations(data.confirmations ?? []);
      setAllConfirmed(data.all_confirmed ?? false);
      setNextRequired(data.next_required ?? null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("samiksha: confirmations load failed", err);
    }
  }, [passportId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const client = getSupabaseClient();

        // 1. Session
        const { data: { session } } = await client.auth.getSession();
        if (!session) { navigate("/login", { replace: true }); return; }

        const token  = session.access_token;
        const userId = session.user.id;

        // 2. User row
        const { data: user, error: userErr } = await client
          .from("users").select("id, gstin, mobile, role, full_name")
          .eq("id", userId).single();
        if (userErr || !user) throw new Error("user-row-missing");

        // 3. Passport
        const { data: pp, error: ppErr } = await client
          .from("passports")
          .select("id, gstin, cn_code, reporting_period_start, reporting_period_end, status, total_tco2e, emission_intensity, aps_score, compliance_type, production_tonnes, scope1_tco2e, scope2_tco2e")
          .eq("id", passportId).single();
        if (ppErr || !pp) throw new Error("passport-missing");

        // 4. Organisation name (best-effort — fall back to GSTIN)
        const { data: orgData } = await client
          .from("organisations")
          .select("legal_name")
          .eq("gstin", user.gstin)
          .single();

        if (!cancelled) {
          setAccessToken(token);
          setUserEmail(session.user?.email || "");
          setUserRow(user);
          setPassport(pp);
          setOrgName(orgData?.legal_name || user.gstin);
          setProdTonnes(pp.production_tonnes != null ? String(pp.production_tonnes) : "");
          setScope1(pp.scope1_tco2e != null ? String(pp.scope1_tco2e) : "");
          setScope2(pp.scope2_tco2e != null ? String(pp.scope2_tco2e) : "");
          await loadConfirmations(token);
          setPhase(PHASE.READY);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("samiksha: load failed", err);
        if (!cancelled) setPhase(PHASE.ERROR);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [navigate, passportId, loadConfirmations]);

  // ── Direct confirm (no OTP) ───────────────────────────────────────────────
  async function handleDirectConfirm(stepType) {
    setConfirming(stepType);
    setConfirmErrors((prev) => ({ ...prev, [stepType]: null }));
    try {
      const res = await fetch(`${API_BASE}/api/passports/${passportId}/confirm/direct`, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:  `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ confirmation_type: stepType }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail ?? PUBLIC_ERROR);
      }
      await loadConfirmations(accessToken);
      // Brief pause — lets the backend status transition commit before we read it back
      await new Promise((r) => setTimeout(r, 700));
      // Re-fetch passport so status badge + preview/payment gating update immediately
      const client = getSupabaseClient();
      const { data: pp } = await client
        .from("passports")
        .select("id, gstin, cn_code, reporting_period_start, reporting_period_end, status, total_tco2e, emission_intensity, aps_score, compliance_type, production_tonnes, scope1_tco2e, scope2_tco2e")
        .eq("id", passportId)
        .single();
      if (pp) setPassport(pp);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("samiksha: confirm failed", err);
      setConfirmErrors((prev) => ({ ...prev, [stepType]: PUBLIC_ERROR }));
    } finally {
      setConfirming(null);
    }
  }

  // ── Data save ─────────────────────────────────────────────────────────────
  async function handleSaveData() {
    setDataSaving(true);
    setDataSaveError("");
    setDataSaved(false);
    try {
      const { error } = await getSupabaseClient()
        .from("passports")
        .update({
          production_tonnes: prodTonnes !== "" ? parseFloat(prodTonnes) : null,
          scope1_tco2e:      scope1     !== "" ? parseFloat(scope1)     : null,
          scope2_tco2e:      scope2     !== "" ? parseFloat(scope2)     : null,
        })
        .eq("id", passportId);
      if (error) throw error;
      setDataSaved(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("samiksha: data save failed", err);
      setDataSaveError(PUBLIC_ERROR);
    } finally {
      setDataSaving(false);
    }
  }

  // ── Sign out ──────────────────────────────────────────────────────────────
  async function handleSignOut() {
    try { await getSupabaseClient().auth.signOut(); } catch { /* swallow */ }
    navigate("/login", { replace: true });
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (phase === PHASE.LOADING) {
    return (
      <Koshtha role="customer" userName={userRow?.full_name || ""} userEmail={userEmail} hideOra>
        <main className="tl-page tl-page--centered">
          <p className="tl-loading">Loading…</p>
        </main>
      </Koshtha>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (phase === PHASE.ERROR) {
    return (
      <Koshtha role="customer" userName={userRow?.full_name || ""} userEmail={userEmail} onSignOut={handleSignOut} hideOra>
        <main className="tl-page tl-page--centered">
          <p className="tl-error">{PUBLIC_ERROR}</p>
        </main>
      </Koshtha>
    );
  }

  // ── Ready ─────────────────────────────────────────────────────────────────
  const co2 = passport.total_tco2e != null
    ? parseFloat(passport.total_tco2e).toFixed(2)
    : null;
  const statusLabel = STATUS_LABELS[passport.status] || passport.status.toUpperCase();
  const period = formatPeriod(passport.reporting_period_start, passport.reporting_period_end);

  // Build a map of confirmed types
  const confirmedMap = {};
  confirmations.forEach((c) => { confirmedMap[c.confirmation_type] = c; });

  return (
    // hideOra: OraPanel is in the page content below (passport-specific context)
    // Koshtha sidebar Ora would duplicate it without passport context.
    <Koshtha
      role="customer"
      userName={userRow?.full_name || ""}
      userEmail={userEmail}
      onSignOut={handleSignOut}
      hideOra
    >
      <main className="tl-page tl-page--form">

        {/* PassPort detail card */}
        <div className="tl-passport-detail">
          <h1 className="tl-passport-detail-cn">CN {passport.cn_code}</h1>
          <p className="tl-passport-detail-meta">
            <span className={`tl-badge tl-badge--${passport.status}`}>{statusLabel}</span>
            <span className="tl-passport-detail-period">{period}</span>
            {co2 && (
              <span className="tl-passport-detail-co2">{co2} tCO₂e</span>
            )}
          </p>
        </div>

        {/* Emissions data entry — draft only */}
        {passport.status === "draft" && (
          <section aria-labelledby="samiksha-data-heading" style={{ marginBottom: "1.5rem" }}>
            <h2 id="samiksha-data-heading" className="tl-confirm-heading">
              Emissions Data
            </h2>
            <div className="tl-data-entry">
              <label className="tl-label" htmlFor="production-tonnes">
                Production Volume (tonnes)
              </label>
              <input
                id="production-tonnes"
                className="tl-input"
                type="number"
                min="0"
                step="any"
                value={prodTonnes}
                onChange={(e) => setProdTonnes(e.target.value)}
                disabled={dataSaving}
              />
              <label className="tl-label" htmlFor="scope1-tco2e">
                Scope 1 Emissions (tCO₂e)
              </label>
              <input
                id="scope1-tco2e"
                className="tl-input"
                type="number"
                min="0"
                step="any"
                value={scope1}
                onChange={(e) => setScope1(e.target.value)}
                disabled={dataSaving}
              />
              <label className="tl-label" htmlFor="scope2-tco2e">
                Scope 2 Emissions (tCO₂e)
              </label>
              <input
                id="scope2-tco2e"
                className="tl-input"
                type="number"
                min="0"
                step="any"
                value={scope2}
                onChange={(e) => setScope2(e.target.value)}
                disabled={dataSaving}
              />
              <button
                className="tl-button-save"
                aria-label="Save data"
                disabled={dataSaving}
                onClick={handleSaveData}
              >
                {dataSaving ? "Saving…" : "Save Data"}
              </button>
              {dataSaved && (
                <p className="tl-success" role="status" style={{ marginTop: "0.5rem" }}>
                  Data saved.
                </p>
              )}
              {dataSaveError && (
                <p className="tl-error" role="alert" style={{ marginTop: "0.5rem" }}>
                  {dataSaveError}
                </p>
              )}
            </div>
          </section>
        )}

        {/* Confirmation panel */}
        <section aria-labelledby="samiksha-confirm-heading">
          <h2 id="samiksha-confirm-heading" className="tl-confirm-heading">
            Confirmations
          </h2>

          <ul className="tl-confirm-steps" role="list">
            {CONFIRM_STEPS.map((step) => {
              const done   = !!confirmedMap[step.type];
              const active = !done && step.type === nextRequired;
              const locked = !done && !active;
              const state  = done ? "done" : active ? "active" : "locked";

              return (
                <ConfirmStep
                  key={step.type}
                  step={step}
                  state={state}
                  confirmedAt={confirmedMap[step.type]?.otp_verified_at}
                  onConfirm={() => handleDirectConfirm(step.type)}
                  confirming={confirming === step.type}
                  error={confirmErrors[step.type] ?? null}
                />
              );
            })}
          </ul>

          {allConfirmed && (
            <div className="tl-all-confirmed" role="status">
              ✓ All confirmations complete. {PASSPORT_LABEL} submitted for sealing.
            </div>
          )}
        </section>

        {/* PassPort preview — visible once confirmed, before and after payment */}
        {(passport.status === "pending_confirmation" || passport.status === "sealed") && (
          <section aria-labelledby="samiksha-preview-heading" style={{ marginTop: "2rem" }}>
            <h2 id="samiksha-preview-heading" className="tl-confirm-heading">
              PassPort Preview
            </h2>
            <div className="tl-pp-preview">
              <div className="tl-pp-preview-header">
                <div>
                  <div className="tl-pp-preview-label">CBAM PassPort</div>
                  <div className="tl-pp-preview-framework">EU Regulation 2023/956</div>
                </div>
                <div className={`tl-pp-preview-status tl-pp-preview-status--${passport.status}`}>
                  {passport.status === "sealed" ? "Sealed" : "Confirmed — Awaiting Seal"}
                </div>
              </div>

              <div className="tl-pp-preview-org-block">
                <p className="tl-pp-preview-org">{orgName}</p>
                <p className="tl-pp-preview-gstin">GSTIN {passport.gstin}</p>
              </div>

              <div className="tl-pp-preview-divider" />

              {/* Hero metric */}
              {passport.emission_intensity != null && (
                <div className="tl-pp-preview-hero">
                  <div className="tl-pp-preview-hero-label">Embedded Emission Intensity</div>
                  <div className="tl-pp-preview-hero-value">
                    {parseFloat(passport.emission_intensity).toFixed(4)}
                    <span className="tl-pp-preview-hero-unit"> tCO₂e / tonne</span>
                  </div>
                  <div className="tl-pp-preview-hero-sub">Carbon embedded per tonne of production — the key CBAM declaration metric</div>
                </div>
              )}

              {/* Data grid */}
              <div className="tl-pp-preview-grid">
                {[
                  ["CN Code",           passport.cn_code],
                  ["Reporting Period",  period],
                  ["Production Volume", passport.production_tonnes != null
                    ? `${Number(passport.production_tonnes).toLocaleString("en-IN")} t`
                    : "—"],
                  ["Total Emissions",   co2 ? `${co2} tCO₂e` : "—"],
                  ["Scope 1",           passport.scope1_tco2e != null
                    ? `${parseFloat(passport.scope1_tco2e).toFixed(2)} tCO₂e`
                    : "—"],
                  ["Scope 2",           passport.scope2_tco2e != null
                    ? `${parseFloat(passport.scope2_tco2e).toFixed(2)} tCO₂e`
                    : "—"],
                ].map(([k, v]) => (
                  <div key={k} className="tl-pp-preview-field">
                    <p className="tl-pp-preview-key">{k}</p>
                    <p className="tl-pp-preview-val">{v}</p>
                  </div>
                ))}
              </div>

              <div className="tl-pp-preview-footer">
                <span className="tl-pp-preview-badge">CBAM Compliant</span>
                <span className="tl-pp-preview-notice">
                  {passport.status === "sealed"
                    ? "This PassPort is sealed and available for download."
                    : "Complete payment below to seal this PassPort and download your certified document."}
                </span>
              </div>
            </div>
          </section>
        )}

        {/* PDF download — gated by Shulka payment; available from pending_confirmation onward */}
        {(passport.status === "pending_confirmation" || passport.status === "sealed") && (
          <div className="tl-download-section" style={{ marginTop: "1.5rem" }}>
            <PaymentGate passportId={passportId} accessToken={accessToken}>
              <button
                className="tl-button-download"
                aria-label="Download PassPort PDF"
                onClick={async () => {
                  const ct = passport.compliance_type;
                  const endpoint = ct === "BRSR"
                    ? `${API_BASE}/api/vivaran/${passportId}/report.pdf`
                    : ct === "CCTS"
                    ? `${API_BASE}/api/pramaan/${passportId}/report.pdf`
                    : `${API_BASE}/api/passports/${passportId}/pdf`;
                  const filename = ct === "BRSR"
                    ? `terralekha-brsr-${passportId.slice(0, 8)}.pdf`
                    : ct === "CCTS"
                    ? `terralekha-ccts-${passportId.slice(0, 8)}.pdf`
                    : `terralekha-passport-${passportId.slice(0, 8)}.pdf`;
                  setDownloadError("");
                  const res = await fetch(endpoint,
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                  );
                  if (!res.ok) {
                    setDownloadError(PUBLIC_ERROR);
                    return;
                  }
                  const blob = await res.blob();
                  const url  = URL.createObjectURL(blob);
                  const a    = document.createElement("a");
                  a.href     = url;
                  a.download = filename;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Download PDF
              </button>
              {downloadError && (
                <p className="tl-error" role="alert" style={{ marginTop: "0.5rem" }}>
                  {downloadError}
                </p>
              )}
            </PaymentGate>
          </div>
        )}

        {/* Ora AI — passport-specific context; in content not sidebar */}
        <OraPanel
          passportId={passportId}
          accessToken={accessToken}
          cnCode={passport.cn_code}
          status={passport.status}
        />

      </main>
    </Koshtha>
  );
}
